import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { ajustarInsumo } from '@/services/movimientosInsumos.service';

const AjustarInsumoModal = ({ isOpen, onClose, onSuccess, insumo, user }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    tipo_ajuste: 'ajuste', // 'ajuste' o 'baja'
    cantidad_ajuste: 0,
    motivo: '',
    observaciones: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar que sea admin
    if (user.role !== 'admin') {
      toast({
        title: 'Acceso Denegado',
        description: 'Solo los administradores pueden ajustar el inventario',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.motivo.trim()) {
      toast({
        title: 'Error',
        description: 'Debes proporcionar un motivo para el ajuste',
        variant: 'destructive'
      });
      return;
    }

    if (formData.cantidad_ajuste === 0) {
      toast({
        title: 'Error',
        description: 'La cantidad de ajuste debe ser diferente de 0',
        variant: 'destructive'
      });
      return;
    }

    const nuevaCantidad = insumo.cantidad_disponible + formData.cantidad_ajuste;
    if (nuevaCantidad < 0) {
      toast({
        title: 'Error',
        description: 'El ajuste resultaría en cantidad negativa',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await ajustarInsumo({
        insumo_id: insumo._id,
        consumible_nombre: insumo.consumible?.nombre || insumo.consumible,
        cantidad_ajuste: formData.cantidad_ajuste,
        tipo_ajuste: formData.tipo_ajuste,
        motivo: formData.motivo,
        observaciones: formData.observaciones,
        usuario_id: user.id,
        usuario_nombre: user.name
      });

      toast({
        title: 'Éxito',
        description: 'Inventario ajustado correctamente',
      });

      // Reset form
      setFormData({
        tipo_ajuste: 'ajuste',
        cantidad_ajuste: 0,
        motivo: '',
        observaciones: ''
      });

      onSuccess();
    } catch (error) {
      console.error('Error al ajustar inventario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo ajustar el inventario',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !insumo) return null;

  const nuevaCantidad = insumo.cantidad_disponible + formData.cantidad_ajuste;
  const esPositivo = formData.cantidad_ajuste > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-orange-50 border-b border-orange-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Ajustar Inventario de Insumo
              </h2>
              <p className="text-sm text-gray-600">
                Solo administradores - Requiere justificación
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Información del Insumo */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Insumo Seleccionado</h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">
                    {insumo.consumible?.nombre || insumo.consumible}
                  </p>
                  <p className="text-sm text-gray-600">Cantidad actual: {insumo.cantidad_disponible}</p>
                </div>
                <Badge className={`${
                  insumo.estado === 'disponible' ? 'bg-green-100 text-green-800' :
                  insumo.estado === 'bajo_stock' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {insumo.estado}
                </Badge>
              </div>
            </div>

            {/* Tipo de Ajuste */}
            <div>
              <Label htmlFor="tipo_ajuste">Tipo de Ajuste</Label>
              <select
                id="tipo_ajuste"
                value={formData.tipo_ajuste}
                onChange={(e) => setFormData({ ...formData, tipo_ajuste: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="ajuste">Ajuste de Inventario</option>
                <option value="baja">Baja por Daño/Vencimiento</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.tipo_ajuste === 'ajuste' 
                  ? 'Normalización de cantidades por conteo físico'
                  : 'Dar de baja insumos dañados o vencidos'}
              </p>
            </div>

            {/* Cantidad de Ajuste */}
            <div>
              <Label htmlFor="cantidad_ajuste">
                Cantidad de Ajuste {formData.tipo_ajuste === 'baja' ? '(usar valores negativos)' : ''}
              </Label>
              <Input
                id="cantidad_ajuste"
                type="number"
                value={formData.cantidad_ajuste}
                onChange={(e) => setFormData({ ...formData, cantidad_ajuste: parseInt(e.target.value) || 0 })}
                placeholder="Ej: -10 para reducir, +10 para aumentar"
                className={`${
                  formData.cantidad_ajuste > 0 ? 'border-green-500' :
                  formData.cantidad_ajuste < 0 ? 'border-red-500' :
                  ''
                }`}
              />
              {formData.cantidad_ajuste !== 0 && (
                <div className={`mt-2 p-3 rounded-lg ${
                  esPositivo ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`text-sm font-medium ${esPositivo ? 'text-green-700' : 'text-red-700'}`}>
                    {esPositivo ? '↑' : '↓'} Cantidad resultante: {nuevaCantidad}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {insumo.cantidad_disponible} {esPositivo ? '+' : ''} {formData.cantidad_ajuste} = {nuevaCantidad}
                  </p>
                </div>
              )}
            </div>

            {/* Motivo (Obligatorio) */}
            <div>
              <Label htmlFor="motivo" className="flex items-center gap-2">
                Motivo <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="motivo"
                value={formData.motivo}
                onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                placeholder="Ej: Conteo físico encontró diferencias, Insumos vencidos, etc."
                rows={3}
                required
                className="border-orange-300 focus:border-orange-500 focus:ring-orange-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Este campo es obligatorio para trazabilidad
              </p>
            </div>

            {/* Observaciones Adicionales */}
            <div>
              <Label htmlFor="observaciones">Observaciones Adicionales</Label>
              <Textarea
                id="observaciones"
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                placeholder="Información adicional sobre el ajuste..."
                rows={2}
              />
            </div>

            {/* Advertencia */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">Advertencia</p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Este ajuste quedará registrado permanentemente en el historial de movimientos.
                    Asegúrate de que la información sea correcta antes de continuar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !formData.motivo.trim() || formData.cantidad_ajuste === 0}
            className="bg-orange-600 hover:bg-orange-700"
          >
            {isLoading ? 'Ajustando...' : 'Confirmar Ajuste'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AjustarInsumoModal;
